/**
 * The persistent shell around every route.
 *
 * This is the old `app/layout.tsx` minus everything that only made sense on a
 * server: the `<html>` and `<body>` elements now live in `index.html`, the font
 * variables come from `@fontsource-variable` imports in `main.tsx` rather than
 * from `next/font`, and the CSP nonce bridge is installed by the entry point
 * instead of by a server-written inline script.
 */
import { Route, Routes } from "react-router";

import { ScrollReveal } from "@/components/ScrollReveal";
import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { WalletProvider } from "@/components/WalletProvider";

import { NotFoundPage } from "@/routes/NotFoundPage";
import { ClaimPage } from "@/routes/ClaimPage";
import { ContractAddressesPage } from "@/routes/ContractAddressesPage";
import { HomePage } from "@/routes/HomePage";
import { SecurityPage } from "@/routes/SecurityPage";
import { StakingPage } from "@/routes/StakingPage";
import { UtilityPage } from "@/routes/UtilityPage";
import { VendorApplyPage } from "@/routes/VendorApplyPage";
import { VendorConfirmPage } from "@/routes/VendorConfirmPage";
import { VendorPage } from "@/routes/VendorPage";
import { VerifyPage } from "@/routes/VerifyPage";
import { VerificationHubPage } from "@/routes/VerificationHubPage";

export function App() {
  return (
    <WalletProvider>
      <ScrollReveal />
      <SiteHeader />
      <main className="site-main mx-auto flex-1 px-5 py-8 sm:px-8 sm:py-12 lg:px-10 lg:py-16">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/claim" element={<ClaimPage />} />
          <Route path="/contract-addresses" element={<ContractAddressesPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/staking" element={<StakingPage />} />

          <Route path="/verify" element={<VerificationHubPage />} />
          <Route path="/verify/:registryId" element={<VerifyPage />} />
          {/* Above /vendor/:registryId in the file, and on its own path rather
              than under it. React Router would rank a static segment over the
              parameter either way, but two routes that shadow each other is a
              trap for whoever adds the third. */}
          <Route path="/daftar-vendor" element={<VendorApplyPage />} />
          <Route path="/daftar-vendor/konfirmasi" element={<VendorConfirmPage />} />
          <Route path="/vendor/:registryId" element={<VendorPage />} />
          <Route path="/utility/:registryId" element={<UtilityPage />} />

          {/*
            There was a rehearsal console here — three pages of buttons that
            drove vendor onboarding one Safe transaction at a time. `onboardVendor`
            made that four-transaction sequence a single call and the server
            files it on its own, so the console had nothing left to drive. The
            only door into vendor onboarding is /daftar-vendor now, and it is a
            form.
          */}

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <SiteFooter />
    </WalletProvider>
  );
}
